/**
 * Typed fetch wrappers for the v1.0 web-chat backend (PR 1). Same-origin in
 * production (nginx serves the static build at the same host as the api), and
 * vite's dev proxy handles cross-port routing in development — either way,
 * `credentials: 'include'` keeps the session cookie attached.
 *
 * Errors fall into two buckets:
 *  - ApiError: a non-2xx response with a parsed JSON body shape we know
 *  - ApiNetworkError: fetch threw (offline, CORS, DNS) — render "couldn't
 *    reach our system" copy.
 *
 * The fetch core lives in @cortex/web-api; the local `call` below binds
 * `credentials: 'include'` so every request keeps the session cookie
 * attached (the shared core defaults to 'same-origin').
 */

import { ApiError, ApiNetworkError, type CallOptions, call as callShared } from '../../index.js';

export { ApiError, ApiNetworkError };

export interface AuthStartResponse {
  status: 'sent' | 'not_recognized' | 'rate_limited';
  expiresInSeconds?: number;
}

export interface AuthVerifyResponse {
  status: 'verified' | 'wrong_code' | 'expired_or_unknown' | 'locked';
  remainingAttempts?: number;
}

export interface ChatPublicStartResponse {
  status: 'started';
  mode: 'public';
  expiresInSeconds: number;
}

export interface ChatMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  body: string;
  sentAt: string;
}

export interface ChatSendResponse {
  conversationId: string;
  messageId: string;
}

export interface ChatMessagesResponse {
  messages: ChatMessage[];
  hasMore: boolean;
}

async function call<TResp>(path: string, init: CallOptions = {}): Promise<TResp> {
  // `...init` last so a call site could still override credentials — same
  // spread order the local core had before the move to @cortex/web-api.
  return callShared<TResp>(path, { credentials: 'include', errorPrefix: 'HTTP', ...init });
}

export async function authStart(phone: string): Promise<AuthStartResponse> {
  return call('/auth/start', {
    method: 'POST',
    body: JSON.stringify({ phone }),
    // 429 carries useful body too — accept it as "ok" for parsing purposes.
    okStatuses: [200, 429],
  });
}

export async function authVerify(phone: string, code: string): Promise<AuthVerifyResponse> {
  return call('/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ phone, code }),
    okStatuses: [200, 401],
  });
}

export async function chatPublicStart(): Promise<ChatPublicStartResponse> {
  return call('/chat/public/start', {
    method: 'POST',
    okStatuses: [200],
  });
}

export async function chatSend(body: string): Promise<ChatSendResponse> {
  return call('/chat/send', {
    method: 'POST',
    body: JSON.stringify({ body }),
    okStatuses: [201],
  });
}

export async function chatMessages(sinceMessageId?: string): Promise<ChatMessagesResponse> {
  const q = sinceMessageId ? `?since=${encodeURIComponent(sinceMessageId)}` : '';
  return call(`/chat/messages${q}`, { method: 'GET' });
}

export interface PmChatStartResponse {
  status: 'started';
  mode: 'public';
  chatId: string;
  expiresInSeconds: number;
}

export interface PmChatMessageItem {
  type: 'message';
  id: string;
  direction: 'inbound' | 'outbound';
  body: string;
  sentAt: string;
  deliveryStatus: 'received' | 'sent' | 'failed' | 'not_attempted';
  system?: boolean;
  actorDisplayName?: string | null;
}

export interface PmChatPickerButton {
  label: string;
  action: string;
  callbackData: string;
  pressed: boolean;
}

export interface PmChatPickerItem {
  type: 'picker';
  id: string;
  approvalKind: string;
  summary: string;
  state: 'awaiting' | 'approved' | 'denied' | 'expired' | 'superseded';
  requestedAt: string;
  decidedAt: string | null;
  decidedLabel: string | null;
  buttonRows: PmChatPickerButton[][];
}

export type PmChatTimelineItem = PmChatMessageItem | PmChatPickerItem;

export interface PmChatMessagesResponse {
  timeline: PmChatTimelineItem[];
}

export interface PmChatSendResponse {
  conversationId: string;
  messageId: string;
  jobId: string;
}

export async function pmChatPublicStart(): Promise<PmChatStartResponse> {
  return call('/pm-chat/public/start', { method: 'POST', okStatuses: [200] });
}

export async function pmChatMessages(): Promise<PmChatMessagesResponse> {
  return call('/pm-chat/messages', { method: 'GET' });
}

export async function pmChatSend(body: string): Promise<PmChatSendResponse> {
  return call('/pm-chat/send', {
    method: 'POST',
    body: JSON.stringify({ body }),
    okStatuses: [201],
  });
}

export async function pmChatTap(callbackData: string): Promise<{ status: 'ok' }> {
  return call('/pm-chat/tap', {
    method: 'POST',
    body: JSON.stringify({ callbackData }),
  });
}

export async function pmChatReset(): Promise<{ status: 'reset' }> {
  return call('/pm-chat/reset', { method: 'POST' });
}

// v1.2 PM-onboarding (PR2) — /setup wizard surface.
// `apps/web` and the api share an origin (nginx serves static + proxies /pm/*),
// so the same fetch wrapper just works.

export interface SetupStartResponse {
  status: 'started';
  pmPhone: string;
  pmEmail: string;
  pmDisplayName: string | null;
}

export interface SetupStateResponse {
  pmPhone: string;
  inviteId: string;
  /**
   * Provider-driven PMS setup contract. Older Buildium runtimes may omit this
   * while the compatibility endpoint is still deployed; the web app renders a
   * constrained Buildium descriptor in that case.
   */
  pms?: SetupPmsDescriptor;
  cards: {
    pms?: { connected: boolean };
    buildium: { connected: boolean };
    email: { connected: boolean };
    calendar: { connected: boolean };
    drive: { connected: boolean };
    phone: { connected: boolean };
  };
  demo: { completed: boolean };
}

export type SetupPmsProvider = 'buildium' | 'appfolio' | 'rentvine';

export interface SetupPmsFieldOption {
  label: string;
  value: string;
}

export interface SetupPmsFieldDescriptor {
  key: string;
  label: string;
  inputType: 'text' | 'password' | 'select';
  required: boolean;
  secret?: boolean;
  placeholder?: string;
  hint?: string;
  defaultValue?: string;
  options?: SetupPmsFieldOption[];
}

export interface SetupPmsDescriptor {
  provider: SetupPmsProvider;
  setupMode: 'self_service' | 'operations_only';
  label: string;
  description: string;
  fields: SetupPmsFieldDescriptor[];
  runtimeConfiguration?: {
    fields: Array<{ key: string; label: string; requiredFor?: string[] }>;
    configuredKeys: string[];
  };
  connection: {
    status: 'missing' | 'disabled' | 'active' | 'error';
    connected: boolean;
    verifiedCapabilities: string[];
    enabledCapabilities: string[];
    grantedCapabilities: string[];
    safeSummary: Record<string, string | number | boolean | null>;
    lastVerifiedAt: string | null;
    lastSyncAt: string | null;
    safeError: string | null;
  };
}

export type SetupPmsResponse =
  | {
      status: 'connected';
      provider: SetupPmsProvider;
      counts: Record<string, number>;
      safeSummary: Record<string, string | number | boolean | null>;
      verifiedCapabilities: string[];
      enabledCapabilities: string[];
      grantedCapabilities: string[];
      lastVerifiedAt: string;
    }
  | {
      status: 'verification_failed';
      provider: SetupPmsProvider;
      errorClass:
        | 'bad_credentials'
        | 'scope_insufficient'
        | 'base_url_mismatch'
        | 'rate_limited'
        | 'unreachable'
        | 'provider_mismatch'
        | 'contract_unverified'
        | 'operations_only'
        | 'connection_changed'
        | 'persistence_failed';
      httpStatus?: number;
      message?: string;
    };

export type SetupBuildiumResponse =
  | { status: 'connected'; counts: { properties: number; vendors: number; leases: number } }
  | {
      status: 'verification_failed';
      errorClass:
        | 'bad_credentials'
        | 'scope_insufficient'
        | 'base_url_mismatch'
        | 'rate_limited'
        | 'unreachable';
      httpStatus?: number;
    };

export type SetupEmailResponse =
  | {
      status: 'connected';
      address: string;
      forwardTo: string;
      accountingInvoiceAddress: string;
    }
  | {
      status: 'cloudflare_failed';
      errorClass: 'destination_not_verified' | 'invalid_destination' | 'cloudflare_unreachable';
      httpStatus?: number;
    };

export type SetupCalendarResponse =
  | { status: 'connected'; calendarId: string; summary: string }
  | {
      status: 'verification_failed';
      errorClass: 'calendar_not_found' | 'not_shared' | 'read_only_share' | 'unreachable';
      httpStatus?: number;
    };

export type SetupDriveResponse =
  | { status: 'connected'; folderId: string; folderName: string }
  | {
      status: 'verification_failed';
      errorClass:
        | 'folder_not_found'
        | 'not_shared'
        | 'not_a_folder'
        | 'read_only_share'
        | 'unreachable';
      httpStatus?: number;
    };

export interface SetupDisconnectResponse {
  status: 'disconnected';
}

export async function pmSetupStart(token: string): Promise<SetupStartResponse> {
  return call('/pm/setup/start', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

export async function pmSetupState(): Promise<SetupStateResponse> {
  return call('/pm/setup/state', { method: 'GET' });
}

export async function pmSetupBuildium(args: {
  clientId: string;
  clientSecret: string;
  baseUrl: string;
}): Promise<SetupBuildiumResponse> {
  return call('/pm/setup/buildium', {
    method: 'POST',
    body: JSON.stringify(args),
    okStatuses: [200, 422],
  });
}

export async function pmSetupPms(args: {
  provider: SetupPmsProvider;
  credentials: Record<string, string>;
}): Promise<SetupPmsResponse> {
  return call('/pm/setup/pms', {
    method: 'POST',
    body: JSON.stringify(args),
    okStatuses: [200, 422],
  });
}

export async function pmSetupEmail(args: {
  forwardTo: string;
  accountingInvoiceAddress: string;
}): Promise<SetupEmailResponse> {
  return call('/pm/setup/email', {
    method: 'POST',
    body: JSON.stringify(args),
    okStatuses: [200, 422],
  });
}

export async function pmSetupCalendar(args: {
  calendarId: string;
}): Promise<SetupCalendarResponse> {
  return call('/pm/setup/calendar', {
    method: 'POST',
    body: JSON.stringify(args),
    okStatuses: [200, 422],
  });
}

export async function pmSetupDrive(args: { folderId: string }): Promise<SetupDriveResponse> {
  return call('/pm/setup/drive', {
    method: 'POST',
    body: JSON.stringify(args),
    okStatuses: [200, 422],
  });
}

export async function pmSetupDisconnectBuildium(): Promise<SetupDisconnectResponse> {
  return call('/pm/setup/buildium', { method: 'DELETE' });
}

export async function pmSetupDisconnectPms(): Promise<SetupDisconnectResponse> {
  return call('/pm/setup/pms', { method: 'DELETE' });
}

export async function pmSetupDisconnectEmail(): Promise<SetupDisconnectResponse> {
  return call('/pm/setup/email', { method: 'DELETE' });
}

export async function pmSetupDisconnectCalendar(): Promise<SetupDisconnectResponse> {
  return call('/pm/setup/calendar', { method: 'DELETE' });
}

export async function pmSetupDisconnectDrive(): Promise<SetupDisconnectResponse> {
  return call('/pm/setup/drive', { method: 'DELETE' });
}
